import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  Dialog,
} from "@mui/material";

import MailOutlineIcon from "@mui/icons-material/MailOutline";

import SettingsIcon from "@mui/icons-material/Settings";

import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import SyncIcon from "@mui/icons-material/Sync";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ToolbarIconButton from "./ToolbarIconButton";
import SwipeableThreadRow from "./SwipeableThreadRow";
import { parseThreadKey, getEmailContact, getEmailDirection } from "./threadGrouping";
import ContactAvatar from "../ContactAvatar";
import {
  getContactDisplayName,
  getContactEmailForAvatar,
} from "../contactDisplay";

import axios from "axios";
import { useUniboxColors } from "../../useUniboxColors";
import { accountColor, accountChipSx } from "../accountColors";

const FILTER_TAB_TONES = {
  all: {
    icon: "#0891b2",
    bg: "rgba(8, 145, 178, 0.14)",
    border: "rgba(8, 145, 178, 0.42)",
  },
  unread: {
    icon: "#0284c7",
    bg: "rgba(14, 165, 233, 0.14)",
    border: "rgba(14, 165, 233, 0.42)",
  },
  read: {
    icon: "#059669",
    bg: "rgba(16, 185, 129, 0.14)",
    border: "rgba(16, 185, 129, 0.42)",
  },
  spam: {
    icon: "#d97706",
    bg: "rgba(245, 158, 11, 0.14)",
    border: "rgba(245, 158, 11, 0.42)",
  },
  trash: {
    icon: "#dc2626",
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.38)",
  },
};

const FILTER_TABS = [
  { value: "all", label: "Todos", Icon: AppsOutlinedIcon },
  { value: "unread", label: "No leídos", Icon: MarkEmailUnreadIcon, inboxOnly: true },
  { value: "read", label: "Leídos", Icon: MarkEmailReadIcon, inboxOnly: true },
  {
    value: "received",
    label: "Recibidos",
    Icon: MoveToInboxOutlinedIcon,
    inboxOnly: true,
  },
  { value: "sent", label: "Enviados", Icon: SendOutlinedIcon, inboxOnly: true },
  {
    value: "favorite",
    label: "Favoritos",
    Icon: StarOutlineIcon,
    inboxOnly: true,
  },
  {
    value: "spam",
    label: "Spam",
    Icon: ReportGmailerrorredOutlinedIcon,
    spamTab: true,
  },
  { value: "trash", label: "Papelera", Icon: DeleteOutlineIcon, trashTab: true },
];

const API = process.env.REACT_APP_API_URL;

/* ================================================
   HELPERS
================================================ */
function getDateBlock(dateStr) {
  if (!dateStr) return "OTROS";
  const date = new Date(dateStr);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const emailDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const diff = (today - emailDay) / 86400000;

  if (diff === 0) return "HOY";
  if (diff === 1) return "AYER";
  if (diff < 7) return "ESTA SEMANA";

  const monthName = date.toLocaleString("es-ES", { month: "long" });
  if (date.getFullYear() === now.getFullYear()) return monthName;

  return `${monthName} ${date.getFullYear()}`;
}

function getBlockSortTime(people) {
  return people.reduce((max, person) => {
    const t = new Date(person.last?.date || 0).getTime();
    return Number.isFinite(t) ? Math.max(max, t) : max;
  }, 0);
}

function sortGroupedPeopleBlocks(result) {
  return Object.fromEntries(
    Object.entries(result)
      .map(([block, people]) => {
        const sortedPeople = [...people].sort(
          (a, b) => new Date(b.last?.date || 0) - new Date(a.last?.date || 0)
        );
        return [block, sortedPeople, getBlockSortTime(sortedPeople)];
      })
      .sort(([, , timeA], [, , timeB]) => {
        const byDate = timeB - timeA;
        if (byDate !== 0) return byDate;
        return 0;
      })
      .map(([block, people]) => [block, people])
  );
}

function getListSubjectLine(last) {
  const subject = String(last?.subject || "").trim();
  if (subject) return subject;
  const snippet = String(last?.snippet || "").trim();
  if (snippet) return snippet.length > 72 ? `${snippet.slice(0, 72)}…` : snippet;
  return "(Sin asunto)";
}

/* ================================================
   COMPONENTE
================================================ */
export default function EmailPanelIzquierdo({
  threads,
  selectedPerson,
  setSelectedPerson,
  search,
  setSearch,
  filter,
  setFilter,
  folder,
  setFolder,
  setOpenAccounts,
  setShowSendPanel, // ADD
  accounts, // ADD
  onEmptyTrash,
  onDeleteConversations,
  isMobile = false,
  listGrouping = "conversation",
  setListGrouping,
  onOpenAttachmentsLibrary,
  loadEmails,
}) {
  const c = useUniboxColors();
  const filterTones = useMemo(
    () => ({
      ...FILTER_TAB_TONES,
      sent: c.sentTone,
      received: c.recvTone,
    }),
    [c.sentTone, c.recvTone]
  );
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [diagnosticBusy, setDiagnosticBusy] = useState(false);
  const [selectedThreadKeys, setSelectedThreadKeys] = useState([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [openSwipeKey, setOpenSwipeKey] = useState(null);
  const [showDeleteThreadsDialog, setShowDeleteThreadsDialog] = useState(false);
  const [pendingDeleteMailCount, setPendingDeleteMailCount] = useState(0);
  const [deleteThreadsBusy, setDeleteThreadsBusy] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const handleOpenMenu = (e) => setAnchorMenu(e.currentTarget);
  const handleCloseMenu = () => setAnchorMenu(null);

  useEffect(() => {
    if (!syncBusy) return;
    const safety = setTimeout(() => setSyncBusy(false), 120_000);
    return () => clearTimeout(safety);
  }, [syncBusy]);
  const [emptyTrashBusy, setEmptyTrashBusy] = useState(false);
  const filterTab =
    folder === "trash" ? "trash" : folder === "spam" ? "spam" : filter;
  const hideInboxFilters = folder === "trash" || folder === "spam";
  const listFilter = hideInboxFilters ? "all" : filter;
  const imapAccounts = useMemo(
    () =>
      (accounts || []).filter(
        (acc) => String(acc.account_type || "").toLowerCase() === "imap"
      ),
    [accounts]
  );

  useEffect(() => {
    setSelectedThreadKeys([]);
    setBulkSelectMode(false);
    setOpenSwipeKey(null);
  }, [folder, filter, search, listGrouping]);

  /* ================= GROUPING ================= */
  const groupedPeople = useMemo(() => {
    const result = {};
    const s = search.toLowerCase();

    for (const threadKey in threads) {
      let mails = threads[threadKey];
      if (!mails.length) continue;

      const parsed = parseThreadKey(threadKey);

      // FILTRO DE BUSQUEDA
      if (s) {
        const matchesEmails = mails.some(
          (e) =>
            e.subject?.toLowerCase().includes(s) ||
            e.snippet?.toLowerCase().includes(s) ||
            e.from?.toLowerCase().includes(s) ||
            e.to_email?.toLowerCase().includes(s)
        );
        if (!matchesEmails) {
          const fallbackContact =
            parsed.contact || getEmailContact(mails[0]) || threadKey;
          const fallbackName =
            listGrouping === "conversation"
              ? mails[0]?.subject || ""
              : getContactDisplayName(mails[0], fallbackContact);
          if (!fallbackName.toLowerCase().includes(s)) continue;
        }
      }

      if (listFilter === "read") mails = mails.filter((e) => e.isRead);
      if (listFilter === "unread") mails = mails.filter((e) => !e.isRead);
      if (listFilter === "favorite") mails = mails.filter((e) => e.isFavorite);
      if (listFilter === "sent") mails = mails.filter((e) => getEmailDirection(e) === "sent");
      if (listFilter === "received") {
        mails = mails.filter((e) => getEmailDirection(e) === "received");
      }
      if (!mails.length) continue;

      const sorted = [...mails].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      const last = sorted[0];
      const contactEmail =
        parsed.contact || getEmailContact(last) || threadKey;
      const contactName = getContactDisplayName(last, contactEmail);
      const contactAvatarEmail = getContactEmailForAvatar(contactEmail, last);
      const displayName =
        listGrouping === "conversation"
          ? last.subject?.trim() || "(Sin asunto)"
          : contactName;
      const displaySecondary =
        listGrouping === "conversation"
          ? contactName
          : getListSubjectLine(last);
      const block = getDateBlock(last.date);
      const unread = mails.filter((x) => !x.isRead).length;
      const distinctAccounts = new Set(
        mails.map((m) => (m.accountEmail || "").toLowerCase()).filter(Boolean)
      );
      const accountList = Array.from(distinctAccounts).sort();
      const threadAccount = parsed.account;
      const accountLabel =
        threadAccount ||
        (accountList.length <= 1
          ? accountList[0] || ""
          : `${accountList.length} cuentas`);
      const accountTooltip = threadAccount
        ? `Cuenta utilizada en este hilo: ${threadAccount}`
        : accountList.length === 0
          ? ""
          : accountList.length === 1
            ? `Cuenta utilizada en este hilo: ${accountList[0]}`
            : `Este hilo mezcla mensajes desde ${accountList.length} cuentas conectadas en UNIBOX: ${accountList.join(" · ")}`;

      if (!result[block]) result[block] = [];

      result[block].push({
        key: threadKey,
        name: displayName,
        secondary: displaySecondary,
        contactEmail,
        contactName,
        contactAvatarEmail,
        isSelfContact: contactEmail === "yo-mismo",
        accountEmail: accountLabel,
        accountTooltip,
        unread,
        last,
        mailCount: mails.length,
      });
    }

    return sortGroupedPeopleBlocks(result);
  }, [threads, search, listFilter, listGrouping]);

  const visibleThreadKeys = useMemo(
    () =>
      Object.values(groupedPeople).flatMap((people) =>
        people.map((p) => p.key)
      ),
    [groupedPeople]
  );

  const allThreadsSelected =
    visibleThreadKeys.length > 0 &&
    visibleThreadKeys.every((key) => selectedThreadKeys.includes(key));

  const selectedMailCount = useMemo(
    () =>
      selectedThreadKeys.reduce(
        (sum, key) => sum + (threads[key]?.length || 0),
        0
      ),
    [selectedThreadKeys, threads]
  );

  const toggleThreadSelection = (key, event) => {
    event?.stopPropagation?.();
    setSelectedThreadKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAllThreads = () => {
    if (allThreadsSelected) {
      setSelectedThreadKeys([]);
      return;
    }
    setSelectedThreadKeys(visibleThreadKeys);
  };

  const clearThreadSelection = () => {
    setSelectedThreadKeys([]);
    setBulkSelectMode(false);
    setOpenSwipeKey(null);
  };

  const countMailsInThreadKeys = (keys) =>
    (keys || []).reduce(
      (sum, key) => sum + (threads[key]?.length || 0),
      0
    );

  const requestDeleteThreads = (keys) => {
    const mailCount = countMailsInThreadKeys(keys);
    if (!mailCount) return;
    setPendingDeleteMailCount(mailCount);
    setSelectedThreadKeys(keys);
    setShowDeleteThreadsDialog(true);
    setOpenSwipeKey(null);
  };

  const confirmDeleteSelectedThreads = async () => {
    if (!selectedThreadKeys.length || deleteThreadsBusy) return;
    try {
      setDeleteThreadsBusy(true);
      await onDeleteConversations?.(selectedThreadKeys);
      setSelectedThreadKeys([]);
      setBulkSelectMode(false);
      setShowDeleteThreadsDialog(false);
      setToast({
        open: true,
        severity: "success",
        message:
          folder === "trash"
            ? "Conversaciones eliminadas definitivamente."
            : "Conversaciones movidas a la papelera.",
      });
    } catch (err) {
      console.error("Error eliminando conversaciones:", err);
      setToast({
        open: true,
        severity: "error",
        message: "No se pudieron eliminar las conversaciones.",
      });
    } finally {
      setDeleteThreadsBusy(false);
    }
  };

  const menuBtnSx = {
    fontSize: 11,
    display: "flex",
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 600,
    color: c.textPrimary,
    borderColor: c.border,
    borderRadius: c.radius,
    py: 0.85,
    "&:hover": {
      bgcolor: c.cardHover,
      borderColor: c.accentBorder,
      color: c.accent,
    },
    "& .MuiButton-startIcon": {
      color: "inherit",
    },
  };

  const menuBtnDangerSx = {
    ...menuBtnSx,
    color: c.isDark ? "#fca5a5" : "#b91c1c",
    borderColor: c.isDark ? "rgba(248, 113, 113, 0.55)" : "#b91c1c",
    "&:hover": {
      bgcolor: c.isDark ? "rgba(248, 113, 113, 0.12)" : "#fef2f2",
      borderColor: c.isDark ? "#f87171" : "#b91c1c",
      color: c.isDark ? "#fecaca" : "#991b1b",
    },
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 340 },
        flexShrink: 0,
        borderRight: { md: `1px solid ${c.border}` },
        display: "flex",
        flexDirection: "column",
        bgcolor: c.panel,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 2.2 }}>
        {/* TITLE */}

        <Box
          sx={{
            pt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            /*  gap: 1, */
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 22 },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: c.textPrimary,
            }}
          >
            MATRIXBOX
          </Typography>
          <Box
            sx={{
              /* pt: 2, */
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            <Tooltip title="Opciones">
              <IconButton
                size="small"
                onClick={handleOpenMenu}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <SettingsIcon sx={{ fontSize: "1.5rem", color: c.textPrimary }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="New Email">
              <IconButton
                size="small"
                onClick={() => setShowSendPanel(true)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <MarkEmailReadIcon
                  sx={{ fontSize: "1.5rem", color: "#00bbcd" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* MENU DESPLEGABLE */}
        <Menu
          anchorEl={anchorMenu}
          open={Boolean(anchorMenu)}
          onClose={handleCloseMenu}
          slotProps={{
            paper: {
              sx: {
                bgcolor: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: c.radiusOuter,
                boxShadow: c.shadowMd,
                minWidth: 188,
                mt: 0.5,
              },
            },
          }}
          MenuListProps={{ sx: { py: 0.75, px: 0.5 } }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              setOpenAccounts(true);
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={<AccountCircleOutlinedIcon />}
            >
              Cuentas
            </Button>
          </MenuItem>

          <MenuItem
            onClick={async () => {
              if (syncBusy) return;
              setSyncBusy(true);
              handleCloseMenu();
              const failedAccounts = [];
              const spamSync = folder === "spam";
              const targets = accounts || [];

              try {
                const results = await Promise.allSettled(
                  targets.map((acc) =>
                    axios.get(`${API}/gmail/sync/${acc.email}`, {
                      timeout: 20_000,
                      params: spamSync
                        ? { notify: "1", limit: "200", spam: "1" }
                        : { notify: "1" },
                    })
                  )
                );

                const junkLines = [];
                results.forEach((result, index) => {
                  if (result.status === "fulfilled") {
                    if (
                      spamSync &&
                      result.value?.data?.queued &&
                      !result.value?.data?.message
                    ) {
                      junkLines.push(
                        `${targets[index]?.email}: sync en segundo plano…`
                      );
                    } else if (spamSync && result.value?.data?.message) {
                      junkLines.push(
                        `${targets[index]?.email}: ${result.value.data.message}`
                      );
                    } else if (
                      spamSync &&
                      result.value?.data?.spamInserted != null
                    ) {
                      junkLines.push(
                        `${targets[index]?.email}: +${result.value.data.spamInserted} spam`
                      );
                    }
                    return;
                  }
                  const acc = targets[index];
                  const code = result.reason?.response?.data?.code;
                  if (code === "GOOGLE_REAUTH_REQUIRED") {
                    failedAccounts.push(acc?.email || "cuenta");
                  } else {
                    failedAccounts.push(`${acc?.email || "cuenta"} (error)`);
                  }
                });

                if (failedAccounts.length > 0) {
                  setToast({
                    open: true,
                    severity: "warning",
                    message: `Sync con errores: ${failedAccounts.join(", ")}`,
                  });
                } else if (spamSync && junkLines.length) {
                  setToast({
                    open: true,
                    severity: junkLines.some((line) => line.includes("+"))
                      ? "success"
                      : "info",
                    message: junkLines.join(" · "),
                  });
                } else {
                  setToast({
                    open: true,
                    severity: "success",
                    message: spamSync
                      ? `Sync Spam lanzado en ${targets.length} cuenta(s). Los resultados aparecerán en breve.`
                      : "Sync lanzado en todas las cuentas. La bandeja se actualizará en breve.",
                  });
                }
                loadEmails?.();
              } catch (err) {
                console.error("Sync general:", err);
                setToast({
                  open: true,
                  severity: "error",
                  message: "No se pudo lanzar el sync.",
                });
              } finally {
                setSyncBusy(false);
              }
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={
                syncBusy ? <CircularProgress size={14} /> : <SyncIcon />
              }
              disabled={syncBusy}
            >
              {syncBusy ? "Sincronizando..." : "Sync"}
            </Button>
          </MenuItem>

          {folder === "spam" && imapAccounts.length > 0 && (
            <MenuItem
              onClick={async () => {
                if (diagnosticBusy) return;
                setDiagnosticBusy(true);
                handleCloseMenu();
                try {
                  for (const acc of imapAccounts) {
                    const email = String(acc.email || "").toLowerCase();
                    const res = await axios.get(
                      `${API}/gmail/imap/diagnostic/${email}`,
                      { timeout: 120_000 }
                    );
                    const d = res.data || {};
                    console.log(`IMAP diagnostic ${email}`, d);
                    if (Array.isArray(d.junkCandidates)) {
                      console.table(d.junkCandidates);
                    }
                    const summary = (d.junkCandidates || [])
                      .map(
                        (row) =>
                          `${row.path}: ${row.messages ?? 0} msgs` +
                          (row.excluded ? " (excl.)" : "")
                      )
                      .join(" · ");
                    setToast({
                      open: true,
                      severity: "info",
                      message: `${email} → ${d.junkResolvedPath || "sin carpeta"}${summary ? ` | ${summary}` : ""} (detalle en F12)`,
                    });
                  }
                } catch (err) {
                  console.error("Diagnóstico IMAP:", err);
                  setToast({
                    open: true,
                    severity: "error",
                    message:
                      err?.response?.data?.details ||
                      err?.response?.data?.error ||
                      "Diagnóstico IMAP fallido.",
                  });
                } finally {
                  setDiagnosticBusy(false);
                }
              }}
              sx={{
                py: 0.4,
                px: 0.75,
                borderRadius: c.radius,
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              <Button
                sx={menuBtnSx}
                variant="outlined"
                fullWidth
                startIcon={
                  diagnosticBusy ? (
                    <CircularProgress size={14} />
                  ) : (
                    <BugReportOutlinedIcon />
                  )
                }
                disabled={diagnosticBusy}
              >
                {diagnosticBusy ? "Diagnosticando…" : "Diagnóstico Spam IMAP"}
              </Button>
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onOpenAttachmentsLibrary?.();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={<AttachFileOutlinedIcon />}
            >
              Archivos adjuntos
            </Button>
          </MenuItem>

          <Divider sx={{ my: 0.5, borderColor: c.border }} />

          <MenuItem
            onClick={() => {
              setListGrouping?.("account");
              handleCloseMenu();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={{
                ...menuBtnSx,
                ...(listGrouping === "account"
                  ? {
                      bgcolor: "#00bbcd",
                      color: "#fff",
                      borderColor: "#00bbcd",
                      "&:hover": {
                        bgcolor: "#00a5b5",
                        borderColor: "#00a5b5",
                        color: "#fff",
                      },
                    }
                  : {}),
              }}
              variant={listGrouping === "account" ? "contained" : "outlined"}
              fullWidth
              startIcon={<SwitchAccountOutlinedIcon />}
              endIcon={
                listGrouping === "account" ? (
                  <CheckIcon sx={{ fontSize: "1rem !important" }} />
                ) : null
              }
            >
              Por cuenta
            </Button>
          </MenuItem>
          <Typography
            sx={{
              px: 1.25,
              pb: 0.35,
              fontSize: 9.5,
              color: c.textDim,
              lineHeight: 1.3,
            }}
          >
            Buzones compartidos (info@, via …) se separan por hilo
          </Typography>

          <MenuItem
            onClick={() => {
              setListGrouping?.("conversation");
              handleCloseMenu();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={{
                ...menuBtnSx,
                ...(listGrouping === "conversation"
                  ? {
                      bgcolor: "#00bbcd",
                      color: "#fff",
                      borderColor: "#00bbcd",
                      "&:hover": {
                        bgcolor: "#00a5b5",
                        borderColor: "#00a5b5",
                        color: "#fff",
                      },
                    }
                  : {}),
              }}
              variant={listGrouping === "conversation" ? "contained" : "outlined"}
              fullWidth
              startIcon={<ForumOutlinedIcon />}
              endIcon={
                listGrouping === "conversation" ? (
                  <CheckIcon sx={{ fontSize: "1rem !important" }} />
                ) : null
              }
            >
              Por conversación
            </Button>
          </MenuItem>
          <Typography
            sx={{
              px: 1.25,
              pb: 0.35,
              fontSize: 9.5,
              color: c.textDim,
              lineHeight: 1.3,
            }}
          >
            Un hilo por asunto (Re:)
          </Typography>

          {folder === "trash" && (
            <MenuItem
              onClick={async () => {
                if (emptyTrashBusy) return;
                const confirmed = window.confirm(
                  "Vas a eliminar definitivamente todos los emails de la papelera. Esta accion no se puede deshacer. Continuar?"
                );
                if (!confirmed) return;

                try {
                  setEmptyTrashBusy(true);
                  await onEmptyTrash?.();
                  setToast({
                    open: true,
                    severity: "success",
                    message: "Papelera vaciada correctamente.",
                  });
                } catch (err) {
                  console.error("Error vaciando papelera:", err);
                  setToast({
                    open: true,
                    severity: "error",
                    message: "No se pudo vaciar la papelera.",
                  });
                } finally {
                  setEmptyTrashBusy(false);
                  handleCloseMenu();
                }
              }}
              sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
            >
              <Button
                sx={menuBtnDangerSx}
                variant="outlined"
                fullWidth
                startIcon={
                  emptyTrashBusy ? <CircularProgress size={14} /> : <DeleteIcon />
                }
                disabled={emptyTrashBusy}
              >
                {emptyTrashBusy ? "Vaciando..." : "Vaciar papelera"}
              </Button>
            </MenuItem>
          )}
        </Menu>

        {/* 🟦 BOTONES NUEVO / SYNC / CUENTAS — REINSERTADOS */}

        {/* BUSCADOR */}
        <TextField
          fullWidth
          placeholder="Buscar…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: c.radiusOuter,
              backgroundColor: c.card,
              boxShadow: c.shadowSm,
              "& fieldset": { borderColor: c.border },
            },
          }}
        />

        {/* FILTROS */}
        <ToggleButtonGroup
          value={filterTab}
          exclusive
          onChange={(e, v) => {
            if (!v) return;
            if (v === "trash") {
              setFolder("trash");
              setFilter("all");
              return;
            }
            if (v === "spam") {
              setFolder("spam");
              setFilter("all");
              return;
            }
            setFolder("inbox");
            setFilter(v);
          }}
          aria-label="Filtrar mensajes"
          sx={{
            mt: 1.25,
            width: "100%",
            display: "flex",
            bgcolor: c.card,
            borderRadius: c.radiusOuter,
            p: 0.35,
            boxShadow: c.shadowSm,
            border: `1px solid ${c.border}`,
            "& .MuiToggleButtonGroup-grouped": {
              flex: 1,
              minWidth: 0,
            },
            "& .MuiToggleButton-root": {
              py: "8px !important",
              px: "2px !important",
              border: 0,
              borderRadius: `${c.radius} !important`,
              mx: 0.1,
              opacity: 0.88,
              transition: "all 0.15s ease",
              "& .MuiSvgIcon-root": { fontSize: 19 },
              "&:hover": {
                opacity: 1,
              },
            },
          }}
        >
          {FILTER_TABS.filter(
            (tab) => !tab.inboxOnly || !hideInboxFilters
          ).map(({ value, label, Icon, trashTab, spamTab }) => {
            const filterKey = trashTab ? "trash" : spamTab ? "spam" : value;
            const tone = filterTones[filterKey] || filterTones.all;

            return (
            <Tooltip key={value} title={label} arrow placement="top">
              <ToggleButton
                value={value}
                aria-label={label}
                data-filter={filterKey}
                sx={{
                  color: tone.icon,
                  "&:hover": {
                    bgcolor: tone.bg,
                    color: tone.icon,
                  },
                  "&.Mui-selected": {
                    bgcolor: tone.bg,
                    color: tone.icon,
                    opacity: 1,
                    boxShadow: `inset 0 0 0 1px ${tone.border}`,
                    "&:hover": {
                      bgcolor: tone.bg,
                    },
                  },
                }}
              >
                <Icon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            );
          })}
        </ToggleButtonGroup>

        <Box sx={{ mt: 0.85, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => {
              if (bulkSelectMode) {
                clearThreadSelection();
                return;
              }
              setBulkSelectMode(true);
              setOpenSwipeKey(null);
            }}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              color: bulkSelectMode ? c.textPrimary : c.accent,
              minWidth: 0,
              px: 0.5,
            }}
          >
            {bulkSelectMode ? "Listo" : "Seleccionar"}
          </Button>
        </Box>

        {bulkSelectMode && selectedThreadKeys.length > 0 && (
          <Box
            sx={{
              mt: 1.25,
              px: 1.15,
              py: 0.9,
              borderRadius: c.radiusOuter,
              bgcolor: c.accentSoft,
              border: `1px solid ${c.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: c.textPrimary,
                  lineHeight: 1.25,
                }}
              >
                {selectedThreadKeys.length}{" "}
                {selectedThreadKeys.length === 1
                  ? "conversación"
                  : "conversaciones"}
              </Typography>
              <Typography
                sx={{ fontSize: 10, color: c.textMuted, mt: 0.2, lineHeight: 1.2 }}
              >
                {selectedMailCount} mensaje
                {selectedMailCount === 1 ? "" : "s"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.45,
                flexShrink: 0,
              }}
            >
              {visibleThreadKeys.length > 1 && (
                <ToolbarIconButton
                  title={allThreadsSelected ? "Quitar todos" : "Seleccionar todos"}
                  onClick={toggleSelectAllThreads}
                  active={allThreadsSelected}
                  size={32}
                  icon={<SelectAllIcon sx={{ fontSize: "0.95rem" }} />}
                />
              )}
              <ToolbarIconButton
                title={
                  folder === "trash"
                    ? "Eliminar definitivamente"
                    : "Mover a papelera"
                }
                onClick={() => {
                  const mailCount = countMailsInThreadKeys(selectedThreadKeys);
                  if (!mailCount) return;
                  setPendingDeleteMailCount(mailCount);
                  setShowDeleteThreadsDialog(true);
                }}
                variant="danger"
                size={32}
                icon={<DeleteIcon sx={{ fontSize: "0.95rem" }} />}
              />
              <ToolbarIconButton
                title="Cancelar selección"
                onClick={clearThreadSelection}
                size={32}
                icon={<CloseIcon sx={{ fontSize: "0.95rem" }} />}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* LISTA DE PERSONAS */}
      <Box
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
        onClick={() => setOpenSwipeKey(null)}
      >
        {Object.entries(groupedPeople).map(([block, people], blockIndex) => (
          <React.Fragment key={block}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                position: "sticky",
                top: 0,
                zIndex: blockIndex + 1,
                bgcolor: c.panel,
                borderBottom: `1px solid ${c.border}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 10,
                  color: c.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {block}
              </Typography>
            </Box>
           
            <List disablePadding>
              {people.map(
                ({
                  key,
                  name,
                  secondary,
                  contactEmail,
                  contactName,
                  contactAvatarEmail,
                  isSelfContact,
                  accountEmail,
                  accountTooltip,
                  unread,
                  last,
                }) => {
                  const threadSelected = selectedThreadKeys.includes(key);
                  const chipStyle = accountChipSx(accountEmail, {
                    metaBg: c.metaBg,
                    border: c.border,
                    textMuted: c.textMuted,
                  });

                  const row = (
                    <ListItemButton
                      selected={selectedPerson === key && !threadSelected}
                      onClick={() => {
                        setOpenSwipeKey(null);
                        setSelectedPerson(key);
                      }}
                      sx={{
                        py: 1.25,
                        pl: bulkSelectMode ? 0.75 : 1.5,
                        pr: 2,
                        mx: 0,
                        mb: 0,
                        width: "100%",
                        borderRadius: c.radius,
                        border: "1px solid",
                        borderColor: "transparent",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.25,
                        bgcolor: threadSelected ? c.accentSoft : c.card,
                        boxShadow: c.shadowSm,
                        "&.Mui-selected": {
                          backgroundColor: c.listSelected,
                          borderColor: c.selectedBorder,
                          boxShadow: c.shadowActive,
                        },
                        "&:hover": {
                          backgroundColor: threadSelected ? c.selected : c.cardHover,
                        },
                      }}
                    >
                      {bulkSelectMode && (
                        <Checkbox
                          size="small"
                          checked={threadSelected}
                          onClick={(e) => toggleThreadSelection(key, e)}
                          sx={{ p: 0.5, mt: 0.5, flexShrink: 0 }}
                        />
                      )}

                      <Box sx={{ position: "relative", mr: 0.5, flexShrink: 0 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: -5,
                        left: 0,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: accountColor(last.accountEmail),
                        border: `2px solid ${c.card}`,
                        zIndex: 5,
                      }}
                    />
                    <ContactAvatar
                      displayName={contactName || name}
                      email={contactAvatarEmail || contactEmail}
                      isSelf={isSelfContact}
                      size={38}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemText
                      sx={{
                        m: 0,
                        "& .MuiListItemText-primary": {
                          fontWeight: 800,
                          fontSize: 13,
                          textOverflow: "ellipsis",
                          display: "block",
                          mr: 3.7,
                        },
                        "& .MuiListItemText-secondary": {
                          fontSize: 11.5,
                          color: c.textMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          mr: 3.5,
                        },
                      }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: unread > 0 ? 800 : 700,
                              color: c.textPrimary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {name}
                          </Typography>
                        </Box>
                      }
                    />
                    {secondary ? (
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 400,
                          color: c.textMuted,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                          mt: 0.15,
                          mr: 3.5,
                        }}
                      >
                        {secondary}
                      </Typography>
                    ) : null}
                    {accountEmail ? (
                      <Tooltip
                        title={accountTooltip || accountEmail}
                        placement="top"
                        enterDelay={400}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 10,
                            border: "1px solid",
                            borderRadius: c.radius,
                            px: 0.8,
                            py: 0.1,
                            mt: 0.5,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "inline-block",
                            cursor: "default",
                            ...chipStyle,
                          }}
                        >
                          {accountEmail}
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </Box>

                  {unread > 0 ? (
                    <Badge
                      badgeContent={unread}
                      sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        "& .MuiBadge-badge": {
                          backgroundColor: "#00bbcd",
                          color: "#fff",
                          fontWeight: 700,
                        },
                      }}
                    />
                  ) : null}
                    </ListItemButton>
                  );

                  return (
                    <Box key={key} sx={{ mx: 1, mb: 0.5 }}>
                      {bulkSelectMode ? (
                        row
                      ) : (
                        <SwipeableThreadRow
                          enabled={!bulkSelectMode}
                          isOpen={openSwipeKey === key}
                          onOpen={() => setOpenSwipeKey(key)}
                          onClose={() =>
                            setOpenSwipeKey((prev) => (prev === key ? null : prev))
                          }
                          onDelete={() => requestDeleteThreads([key])}
                        >
                          {row}
                        </SwipeableThreadRow>
                      )}
                    </Box>
                  );
                }
              )}
            </List>
          </React.Fragment>
        ))}
      </Box>

      <Dialog
        open={showDeleteThreadsDialog}
        onClose={() => !deleteThreadsBusy && setShowDeleteThreadsDialog(false)}
      >
        <Box sx={{ p: 3, maxWidth: 420 }}>
          <Typography variant="h6">¿Eliminar conversaciones seleccionadas?</Typography>
          <Typography sx={{ color: c.textMuted, mt: 1, fontSize: 14 }}>
            {folder === "trash"
              ? `Se eliminarán definitivamente ${pendingDeleteMailCount} mensaje(s) de ${selectedThreadKeys.length} conversación(es).`
              : `Se moverán a la papelera ${pendingDeleteMailCount} mensaje(s) de ${selectedThreadKeys.length} conversación(es).`}
          </Typography>
          {deleteThreadsBusy ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mt: 2,
                py: 1,
                color: c.textMuted,
              }}
            >
              <CircularProgress size={20} />
              <Typography sx={{ fontSize: 14 }}>Eliminando conversaciones...</Typography>
            </Box>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button
              onClick={() => setShowDeleteThreadsDialog(false)}
              disabled={deleteThreadsBusy}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteSelectedThreads}
              disabled={deleteThreadsBusy}
              startIcon={
                deleteThreadsBusy ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {deleteThreadsBusy ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
